import React, { useEffect, useState } from 'react'
import { Crown, Coins, Loader2, Sparkles, QrCode, Copy, Check } from 'lucide-react'
import { membershipService, type MembershipMe } from '../../services/membership/membershipService'

// Tên ngân hàng theo mã VietQR (hiển thị thân thiện trong popup QR).
const BANK_NAMES: Record<string, string> = {
  STB: 'Sacombank', VCB: 'Vietcombank', TCB: 'Techcombank', MB: 'MB Bank', MBBANK: 'MB Bank',
  BIDV: 'BIDV', ACB: 'ACB', VPB: 'VPBank', VIB: 'VIB', TPB: 'TPBank', VTB: 'VietinBank', AGRIBANK: 'Agribank',
}
const bankName = (id?: string) => (id ? (BANK_NAMES[id.toUpperCase()] || id) : '')

// Thẻ tài khoản: trạng thái PRO + số điểm + nạp điểm (QR). showPoints=false cho eng/lab.
const MembershipCard: React.FC<{ showPoints?: boolean }> = ({ showPoints = true }) => {
  const [me, setMe] = useState<MembershipMe | null>(null)
  const [loading, setLoading] = useState(true)
  const [cfg, setCfg] = useState<any>(null)
  const [topup, setTopup] = useState<any | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [qrBig, setQrBig] = useState<any | null>(null)   // popup QR lớn (chuyển khoản)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const copyVal = (k: string, v: string) => { navigator.clipboard?.writeText(v).catch(() => {}); setCopiedKey(k); setTimeout(() => setCopiedKey(null), 1500) }

  const load = async () => {
    setLoading(true)
    try { setMe(await membershipService.me()) } catch { setMe(null) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useEffect(() => { membershipService.prices().then(setCfg).catch(() => {}) }, [])

  const buy = async (vnd: number) => {
    setBusy(true)
    try { setTopup(await membershipService.topupRequest(vnd)) } catch (e: any) { alert(e.message) } finally { setBusy(false) }
  }
  const copyMemo = () => { if (topup?.memo) { navigator.clipboard?.writeText(topup.memo).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1500) } }

  if (loading) return <div className="flex items-center gap-2 text-gray-400 text-sm p-4"><Loader2 className="animate-spin w-4 h-4" /> Đang tải tài khoản…</div>

  const pro = me?.sites?.ebook
  const active = !!pro?.active
  const isTrial = pro?.tier === 'trial'
  const proPrice = cfg?.pro_price_vnd ?? 1499000
  const proMonthly = cfg?.pro_monthly_points ?? 100
  const zalo = cfg?.zalo ?? '0978531191'
  const bank = cfg?.bank
  const email = me?.email || ''
  const proQr = bank ? `https://img.vietqr.io/image/${bank.bank_id}-${bank.account}-compact2.png?amount=${proPrice}&addInfo=${encodeURIComponent('PRO ' + email)}&accountName=${encodeURIComponent(bank.account_name)}` : null

  return (
    <div className="space-y-4">
      {/* PRO status */}
      <div className={`rounded-2xl border p-4 ${active ? (isTrial ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200') : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${active ? (isTrial ? 'bg-amber-500' : 'bg-emerald-600') : 'bg-gray-400'}`}><Crown size={22} /></span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-black uppercase tracking-wide text-gray-500">Gói tài khoản</div>
            <div className="text-lg font-black text-gray-900">
              {active ? (isTrial ? 'PRO Dùng thử' : 'PRO') : 'Chưa kích hoạt PRO'}
            </div>
            <div className="text-xs text-gray-500">
              {active ? ((pro?.days_left ?? 0) < 0 ? 'Không giới hạn' : `Còn ${pro?.days_left} ngày${pro?.expires_at ? ` · hết hạn ${new Date(pro.expires_at).toLocaleDateString('vi-VN')}` : ''}`) : 'Chưa kích hoạt — xem hướng dẫn kích hoạt PRO bên dưới.'}
            </div>
          </div>
        </div>
      </div>

      {/* Hướng dẫn KÍCH HOẠT PRO — dùng tài khoản sẵn có + chuyển khoản + nhắn Zalo */}
      {(!active || isTrial) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <div className="font-black text-amber-800 flex items-center gap-2"><Crown size={18} /> Kích hoạt gói PRO</div>
          <div className="text-sm text-gray-700 mt-1">
            <b>{(proPrice / 1000).toLocaleString('vi-VN')}k/năm</b> ({proPrice.toLocaleString('vi-VN')}đ) — mở khoá tạo bài giảng & ngân hàng đề (Ebook + PMaker) + <b>{proMonthly} điểm/tháng</b>.
          </div>
          <ol className="text-[13px] text-gray-700 mt-2 space-y-1 list-decimal pl-5">
            <li>Dùng <b>chính tài khoản đang đăng nhập</b>{email && <> (<span className="font-mono text-gray-900">{email}</span>)</> } — không cần tạo tài khoản mới.</li>
            <li>Chuyển khoản <b>{proPrice.toLocaleString('vi-VN')}đ</b>{bank ? <> tới <b>{bank.account_name}</b> · {bank.bank_id} <b className="font-mono">{bank.account}</b></> : ''} — nội dung CK ghi <b>email tài khoản</b>.</li>
            <li>Chuyển xong, nhắn <b>Zalo {zalo}</b> kèm email tài khoản → được kích hoạt <b>PRO 365 ngày</b>.</li>
          </ol>
          {proQr && (
            <div className="mt-3">
              <div className="text-[12px] text-gray-600 mb-2"><b>Bấm vào QR để phóng to</b>. Quét chuyển đúng <b>{proPrice.toLocaleString('vi-VN')}đ</b> (đã điền sẵn nội dung), rồi nhắn Zalo.</div>
              <button type="button" className="block" onClick={() => setQrBig({ url: proQr, title: 'Chuyển khoản kích hoạt PRO', amount: proPrice, memo: `PRO ${email}`, bankId: bank?.bank_id, account: bank?.account, accountName: bank?.account_name })}>
                <img src={proQr} alt="VietQR PRO" className="w-40 h-40 rounded-lg border bg-white cursor-pointer hover:opacity-90 transition" />
              </button>
            </div>
          )}
          <a href={`https://zalo.me/${zalo}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">Nhắn Zalo {zalo}</a>
        </div>
      )}

      {/* Points */}
      {showPoints && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Coins size={22} /></span>
              <div>
                <div className="text-[11px] font-black uppercase tracking-wide text-indigo-500">Số điểm</div>
                <div className="text-2xl font-black text-indigo-700">{me?.points ?? 0} <span className="text-sm font-medium text-gray-400">điểm khả dụng</span></div>
                {(me?.monthly_allowance || 0) > 0 && (
                  <div className="text-[11px] font-bold text-amber-600">Hạn mức tháng {me?.allowance_remaining ?? 0}/{me?.monthly_allowance} + ví mua {me?.wallet_balance ?? 0}</div>
                )}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Điểm dùng cho AI: tạo bài giảng (10đ), số hoá PDF (1đ/trang), sinh biến thể (1đ), lồng tiếng (1đ). 1 điểm = 1.000đ. Trừ <b>hạn mức tháng</b> trước, hết mới trừ <b>ví mua thêm</b>.</p>

          {/* Nạp điểm */}
          {!topup ? (
            <div className="mt-3">
              <div className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5"><Sparkles size={14} className="text-indigo-500" /> Nạp điểm</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(cfg?.packages || []).map((p: any) => (
                  <button key={p.vnd} disabled={busy} onClick={() => buy(p.vnd)}
                    className="rounded-xl border border-indigo-200 bg-white p-2.5 text-center hover:border-indigo-400 hover:shadow-sm transition disabled:opacity-50">
                    <div className="font-black text-indigo-700">{p.points}đ</div>
                    <div className="text-[11px] text-gray-500">{(p.vnd / 1000).toLocaleString('vi-VN')}k{p.bonus_pct ? ` · +${p.bonus_pct}%` : ''}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-3 rounded-xl border border-indigo-200 bg-white p-3">
              <div className="flex items-center gap-2 text-sm font-bold text-indigo-700 mb-2"><QrCode size={16} /> Quét QR để nạp {(topup.amount_vnd / 1000).toLocaleString('vi-VN')}k → {topup.points} điểm</div>
              {topup.vietqr?.qr_image ? (
                <button type="button" className="block mx-auto" onClick={() => setQrBig({ url: topup.vietqr.qr_image, title: `Nạp ${(topup.amount_vnd / 1000).toLocaleString('vi-VN')}k → ${topup.points} điểm`, amount: topup.amount_vnd, memo: topup.memo, bankId: topup.vietqr.bank_id, account: topup.vietqr.account, accountName: topup.vietqr.account_name })}>
                  <img src={topup.vietqr.qr_image} alt="VietQR" className="w-48 h-48 mx-auto rounded-lg border cursor-pointer hover:opacity-90 transition" />
                  <span className="block text-[11px] text-indigo-600 font-bold mt-1">Bấm để phóng to QR</span>
                </button>
              ) : (
                <p className="text-xs text-amber-600">Chưa cấu hình tài khoản nhận. Liên hệ quản trị.</p>
              )}
              <div className="text-xs text-gray-600 mt-2 flex items-center justify-center gap-2">
                Nội dung CK: <b className="font-mono">{topup.memo}</b>
                <button onClick={copyMemo} className="p-1 text-gray-400 hover:text-indigo-600">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
              </div>
              <p className="text-[11px] text-center text-gray-500 mt-2">Chuyển khoản đúng nội dung → quản trị duyệt → điểm được cộng. (Sắp có: cộng tự động.)</p>
              <div className="flex justify-center gap-2 mt-2">
                <button onClick={() => { setTopup(null); load() }} className="text-xs font-bold text-indigo-600 hover:underline">Tôi đã chuyển — Đóng</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Popup QR LỚN — bấm vào bất kỳ QR chuyển khoản nào để mở */}
      {qrBig && (
        <div onClick={() => setQrBig(null)} className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-2xl">
            <button onClick={() => setQrBig(null)} className="absolute right-3 top-3 w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 text-2xl leading-none">×</button>
            <div className="font-black text-gray-900 mb-3">{qrBig.title}</div>
            {qrBig.url && <img src={qrBig.url} alt="QR chuyển khoản" className="w-64 h-64 mx-auto rounded-lg border bg-white" />}
            <div className="text-sm text-left mt-4 space-y-1.5">
              {qrBig.accountName && <div className="flex justify-between gap-2"><span className="text-gray-500">Chủ tài khoản</span><b>{qrBig.accountName}</b></div>}
              {qrBig.account && <div className="flex justify-between gap-2 items-center"><span className="text-gray-500">Số tài khoản</span><span><b className="font-mono">{qrBig.account}</b> <button onClick={() => copyVal('acc', qrBig.account)} className="align-middle text-gray-400 hover:text-indigo-600">{copiedKey === 'acc' ? <Check size={14} className="inline" /> : <Copy size={14} className="inline" />}</button></span></div>}
              {qrBig.bankId && <div className="flex justify-between gap-2"><span className="text-gray-500">Ngân hàng</span><b>{bankName(qrBig.bankId)}</b></div>}
              {qrBig.amount && <div className="flex justify-between gap-2"><span className="text-gray-500">Số tiền</span><b className="text-indigo-700">{qrBig.amount.toLocaleString('vi-VN')}đ</b></div>}
              {qrBig.memo && <div className="flex justify-between gap-2 items-center"><span className="text-gray-500">Nội dung CK</span><span><b className="font-mono">{qrBig.memo}</b> <button onClick={() => copyVal('memo', qrBig.memo)} className="align-middle text-gray-400 hover:text-indigo-600">{copiedKey === 'memo' ? <Check size={14} className="inline" /> : <Copy size={14} className="inline" />}</button></span></div>}
            </div>
            <p className="text-[11px] text-gray-500 mt-3">Mở app ngân hàng → quét QR (đã điền sẵn số tiền & nội dung) → xác nhận. Vui lòng chuyển <b>đúng nội dung</b>.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MembershipCard
